import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CircularProgress, LinearProgress, MenuItem, Select, Skeleton,
  Stack, Table, TableBody, TableContainer, TableCell, TableHead, TableRow, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PremiumHeader from "./components/PremiumHeader";
import SummaryCards from "./components/SummaryCards";
import FilterPanel, { defaultFilter, FilterState } from "./components/FilterPanel";
import StudentRow from "./components/StudentRow";
import { reportsApi, Meta } from "../shared/api";
import { useDebounced } from "../shared/useDebounced";
import { Row } from "./types";

// ── Query params that both endpoints share ────────────────────────────────
// Kept as a pure function of the filter state so it can be memoized and used
// as the effect dep, guaranteeing "only rerun when filter values actually
// change" — no per-render fetch storm.
function toParams(f: FilterState, page: number, perPage: number) {
  return {
    search: f.search || undefined,
    student_status: f.student_status || undefined,
    has_subjects: f.has_subjects || undefined,
    school_grade_id: f.school_grade_id.length ? f.school_grade_id : undefined,
    enrolled_grade_id: f.enrolled_grade_id.length ? f.enrolled_grade_id : undefined,
    subject_id: f.subject_id.length ? f.subject_id : undefined,
    study_mode: f.study_mode.length ? f.study_mode : undefined,
    location_id: f.location_id.length ? f.location_id : undefined,
    page, per_page: perPage,
  };
}

// Filter set that drives the SUMMARY cards. It ignores the page/perPage
// (summary is page-independent) so switching pages doesn't refetch the cards.
function toSummaryParams(f: FilterState) {
  return {
    search: f.search || undefined,
    student_status: f.student_status || undefined,
    has_subjects: f.has_subjects || undefined,
    school_grade_id: f.school_grade_id.length ? f.school_grade_id : undefined,
    enrolled_grade_id: f.enrolled_grade_id.length ? f.enrolled_grade_id : undefined,
    subject_id: f.subject_id.length ? f.subject_id : undefined,
    study_mode: f.study_mode.length ? f.study_mode : undefined,
    location_id: f.location_id.length ? f.location_id : undefined,
  };
}

export default function StudentSubjectsReportPage() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // "Active" default as requested — the report opens focused on the students
  // who are actually operating in the school today.
  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter, student_status: "active" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Search is debounced so the list only fires ~400 ms after the last keystroke.
  const debouncedSearch = useDebounced(filter.search, 400);
  const effectiveFilter = useMemo(
    () => ({ ...filter, search: debouncedSearch }),
    [filter, debouncedSearch]
  );

  const listParams  = useMemo(() => toParams(effectiveFilter, page, perPage), [effectiveFilter, page, perPage]);
  // Summary key intentionally excludes page/perPage so pagination alone
  // never triggers a summary refetch.
  const summaryKey  = useMemo(() => JSON.stringify(toSummaryParams(effectiveFilter)), [effectiveFilter]);

  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "csv" | "pdf">(null);

  // Filter dropdown options — loaded once when the page opens.
  const [options, setOptions] = useState<{
    grades: { id: number; label: string }[];
    subjects: { id: number; label: string }[];
    locations: { id: number; label: string }[];
    study_modes: { value: string; label: string }[];
  }>({ grades: [], subjects: [], locations: [], study_modes: [] });

  const listReqRef = useRef(0);
  const summaryReqRef = useRef(0);
  const tableTopRef = useRef<HTMLDivElement | null>(null);

  // ── One-shot options load ─────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    reportsApi.studentSubjectsOptions()
      .then((res) => { if (alive) setOptions(res); })
      .catch((e: any) => { if (alive) setError(e.message || String(e)); })
      .finally(() => { if (alive) setLoadingOptions(false); });
    return () => { alive = false; };
  }, []);

  // ── List loader — runs on filter or page change ───────────────────────
  const loadList = useCallback(async () => {
    const token = ++listReqRef.current;
    setLoadingList(true); setError(null);
    try {
      const res = await reportsApi.studentSubjects(listParams);
      if (token !== listReqRef.current) return; // stale
      setRows(res.data);
      setMeta(res.meta);
      setRefreshedAt(new Date());
    } catch (e: any) {
      if (token === listReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === listReqRef.current) setLoadingList(false);
    }
  }, [listParams]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Summary loader — runs only when the filter changes, not on paging ──
  useEffect(() => {
    const token = ++summaryReqRef.current;
    setLoadingSummary(true);
    reportsApi.studentSubjectsSummary(JSON.parse(summaryKey))
      .then((res) => {
        if (token !== summaryReqRef.current) return;
        setSummary(res);
      })
      .catch((e: any) => {
        if (token === summaryReqRef.current) setError(e.message || String(e));
      })
      .finally(() => {
        if (token === summaryReqRef.current) setLoadingSummary(false);
      });
  }, [summaryKey]);

  // Any filter change → jump back to page 1. Kept out of the params memo to
  // avoid triggering a page-1 fetch on the OLD filter set.
  useEffect(() => { setPage(1); }, [summaryKey]);

  // ── Actions ───────────────────────────────────────────────────────────
  const doExport = async (kind: "csv" | "pdf") => {
    setExporting(kind); setError(null);
    try {
      const fn = kind === "csv" ? reportsApi.studentSubjectsCsv : reportsApi.studentSubjectsPdf;
      await fn(toParams(effectiveFilter, 1, perPage)); // filters only; server handles all rows
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const refresh = () => { loadList(); };

  const gotoPage = (p: number) => {
    setPage(p);
    // Scroll table into view after next paint so the user sees the new rows.
    setTimeout(() => tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <Box p={2}>
      <PremiumHeader
        refreshedAt={refreshedAt}
        loading={loadingList}
        exporting={exporting}
        onRefresh={refresh}
        onCsv={() => doExport("csv")}
        onPdf={() => doExport("pdf")}
      />

      <SummaryCards summary={summary} loading={loadingSummary} />

      <FilterPanel
        value={filter}
        onChange={setFilter}
        onClear={() => setFilter({ ...defaultFilter })}
        loading={loadingList || loadingOptions}
        gradeOptions={options.grades}
        subjectOptions={options.subjects}
        locationOptions={options.locations}
        studyModeOptions={options.study_modes}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* Results card */}
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", position: "relative" }}>
        {loadingList && (
          // Slim linear bar over the top of the table — the previous rows stay
          // visible while the new page arrives.
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }} />
        )}

        {isMobile ? (
          // Mobile: card-per-row instead of a horizontally-scrolling table.
          <Box p={1}>
            {rows.length === 0 && !loadingList && (
              <EmptyState onClear={() => setFilter({ ...defaultFilter })} />
            )}
            {rows.map((r) => (
              <MobileCard key={r.student_id} row={r}
                onView={(id) => nav(`/student-history/${id}`)}
                onManageSubjects={(id) => nav(`/student-history/${id}`)} />
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 640 }}>
            <div ref={tableTopRef} />
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {["Student", "School Grade", "Enrolled Subjects", "Study Mode", "Location", "Status", ""]
                    .map((h) => (
                      <TableCell key={h}
                        sx={{ fontWeight: 700, background: "#f8fafc", color: "#1b3a6b", textTransform: "uppercase", fontSize: 11, letterSpacing: 0.4 }}>
                        {h}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList && rows.length === 0 && (
                  <>
                    {[...Array(6)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(7)].map((_, j) => (
                          <TableCell key={j}><Skeleton height={26} /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                )}
                {!loadingList && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState onClear={() => setFilter({ ...defaultFilter })} />
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <StudentRow key={r.student_id} row={r}
                    onView={(id) => nav(`/student-history/${id}`)}
                    onManageSubjects={(id) => nav(`/student-history/${id}`)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer / pagination */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center"
          sx={{ p: 1.5, borderTop: "1px solid #eef2f7", background: "#fbfcfe" }}>
          <Typography variant="body2" color="text.secondary" flex={1}>
            {meta && meta.total > 0
              ? `Showing ${(meta.from ?? 0).toLocaleString()}–${(meta.to ?? 0).toLocaleString()} of ${meta.total.toLocaleString()} students`
              : "—"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
            <Select size="small" value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              sx={{ minWidth: 80 }}>
              {[25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
            <Button size="small" disabled={!meta || page <= 1 || loadingList}
              onClick={() => gotoPage(page - 1)}>Previous</Button>
            <Typography variant="body2">
              {meta ? `${meta.current_page} / ${meta.last_page}` : "—"}
            </Typography>
            <Button size="small" disabled={!meta || page >= (meta?.last_page ?? 1) || loadingList}
              onClick={() => gotoPage(page + 1)}>Next</Button>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
}

// ── Empty + mobile helpers ────────────────────────────────────────────────

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Stack alignItems="center" spacing={1} py={4}>
      <Typography fontWeight={600}>No students found</Typography>
      <Typography variant="body2" color="text.secondary">
        Try changing or clearing the selected filters.
      </Typography>
      <Button variant="outlined" size="small" onClick={onClear}>Clear filters</Button>
    </Stack>
  );
}

function MobileCard({ row, onView, onManageSubjects }: { row: Row; onView: (id: number) => void; onManageSubjects: (id: number) => void; }) {
  return (
    <Card variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography fontWeight={600} flex={1}
          onClick={() => onView(row.student_id)}
          sx={{ color: "#1b3a6b", cursor: "pointer" }}>
          {row.student_name || "—"}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        ID {row.student_id} · {row.school_grade ? `Grade ${row.school_grade}` : "—"} · {row.location || "Not assigned"}
      </Typography>
      <Box mt={1}>
        {row.enrolled_subjects.length === 0
          ? <Typography variant="body2" color="warning.main">No enrolled subjects</Typography>
          : row.enrolled_subjects.map((s, i) => (
              <Typography key={i} variant="body2">
                {s.grade_name} — {s.subject_name}
              </Typography>
            ))}
      </Box>
    </Card>
  );
}
